import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionSummary, Alert, Box, Typography } from '@mui/material'
import React, { useState } from 'react'
import {
  useGetDisbursementAccountsQuery,
  useGetDisbursementFeesQuery,
  useGetPendingDisbursementInstructionsQuery
} from '../../../../api/disbursements/disbursementApi'
import type { AssetHolderUpdateDto } from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import { type LedgerAccountDetails } from '../../../../api/schema-dtos/AssetHolderFinancePayloadDtos'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import ErrorComponent from '../../../shared/components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../shared/components/ui-widgets/LoadingComponent'
import type { AssetHolderIdProps } from '../../pages/DashboardHome'
import CreateDisbursementInstructionComponent from './CreateDisbursementInstructionComponent'
import DisbursementFeesComponent from './DisbursementFeesComponent'
import DisbursementInstructionsComponent from './DisbursementInstructionsComponent'

interface ComponentProps extends AssetHolderIdProps {
  assetHolder: AssetHolderUpdateDto
  wallets: LedgerAccountDetails[]
  verticalSpacing: number

  maxCardWidth: number
}

const AccordionCashOuts: React.FC<ComponentProps> = ({ assetHolderId, wallets, assetHolder, verticalSpacing, maxCardWidth }: ComponentProps) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const getDisbursementFeesApiResult = useGetDisbursementFeesQuery()
  const getPendingDisbursementInstructions = useGetPendingDisbursementInstructionsQuery(assetHolderId)
  const getDisbursementAccountsApiResult = useGetDisbursementAccountsQuery(assetHolderId)

  const accordionIdentifier = `dashboard-exchange-instructions-${assetHolderId}`

  return (
    <>
      <Accordion sx={{ px: 4, pt: 2 }} expanded={expanded} onChange={(evt, expanded) => { setExpanded(expanded) }} >

        <AccordionSummary expandIcon={<ExpandMoreIcon color={'primary'}/>}
                          aria-controls={accordionIdentifier}
                          id={accordionIdentifier} sx={{ px: 0, pb: 2 }}>
          {/* <Typography variant={'body1'}>Donate all your balances to a selected charity</Typography> */}
          <Typography variant={'h3'} sx={{ mb: 1 }}>Cash Outs</Typography>
        </AccordionSummary>

        <Box sx={{ pb: 4 }}>

          {getPendingDisbursementInstructions.isLoading && <LoadingComponent/>}
          {getPendingDisbursementInstructions.isError &&
            <ErrorComponent message={RtkQueryHelper.mapResponseError(getPendingDisbursementInstructions.error)}/>}

          {!assetHolder.hasPassedKyc &&
            <Box sx={{ my: verticalSpacing, maxWidth: maxCardWidth }}>
              <Alert severity="warning" sx={{ mt: 3 }}>
                <b>Note:</b> Please complete your KYC on the &apos;My Profile&apos; tab in order to add/update cash out account details or to schedule a
                cash out request.
              </Alert>
            </Box>
          }

          {getPendingDisbursementInstructions.isSuccess &&
            <DisbursementInstructionsComponent
              assetHolderId={assetHolderId}
              pendingDisbursementInstructions={getPendingDisbursementInstructions.data}
              maxCardWidth={maxCardWidth}
            />
          }

          <Box>
            {(getDisbursementFeesApiResult.data !== undefined && getDisbursementAccountsApiResult.data !== undefined) &&
              <CreateDisbursementInstructionComponent
                assetHolderId={assetHolderId}
                disbursementFees={getDisbursementFeesApiResult.data}
                disbursementAccounts={getDisbursementAccountsApiResult.data}
                wallets={wallets}
                hasPassedKyc={assetHolder.hasPassedKyc}
                maxCardWidth={maxCardWidth}
              />
            }

            {(getDisbursementFeesApiResult.isLoading) && <LoadingComponent/>}
            {getDisbursementFeesApiResult.isError &&
              <ErrorComponent message={RtkQueryHelper.mapResponseError(getDisbursementFeesApiResult.error)}/>}

            {getDisbursementFeesApiResult.data !== undefined &&
              <DisbursementFeesComponent disbursementFees={getDisbursementFeesApiResult.data}/>
            }
          </Box>

        </Box>

      </Accordion>
    </>
  )
}

export default AccordionCashOuts
